import React, { useEffect, useState } from "react";
import DraggablePanel from "../ui/DraggablePanel";
import { useBeamTrussStore } from "../../hooks/useBeamTrussManagement";
import { useTrussStore } from "../../hooks/useTrussManagement";
import { useModelStore } from "../../store/useModelStore";

type ReferenceNode = "S_Node" | "E_Node" | "Center";
type LoadType = "distributed1" | "distributed2" | "concentrated" | "twisted";

import { useUnitStore } from "../../store/useUnitStore";
import { BeamTrussType } from "@/types/beamTruss";

interface LoadValues {
  dx?: number;
  dy?: number;
  dz?: number;

  dxStart?: number;
  dxEnd?: number;
  dyStart?: number;
  dyEnd?: number;
  dzStart?: number;
  dzEnd?: number;

  referenceNode?: ReferenceNode;
  percentageFromNode?: number;
  fx?: number;
  fy?: number;
  fz?: number;

  // Twisted load properties
  torque?: number;
  torqueDirection?: "clockwise" | "anticlockwise";
}

interface Props {
  beamId?: string;
  beamIds?: string[] | null;
  nodeId?: string;
  nodeIds?: string[];
  position?: { x: number; y: number };
  inlineMode?: "beam" | "node" | null; // specify type of inline mode
  setInlineMode?: (value: "beam" | "node" | null) => void; // handle both types
  directMode?: boolean;
  setDirectMode?: (value: boolean) => void;
  onClose: () => void;
}

const formatLoadTypeName = (type: LoadType): string => {
  switch (type) {
    case "distributed1":
      return "Distributed1";
    case "distributed2":
      return "Distributed2";
    case "concentrated":
      return "Concentrated";
    case "twisted":
      return "Twisted";
    default:
      return type;
  }
};

export const PredefinedLoadManager: React.FC<Props> = ({
  beamId,
  beamIds,
  nodeId,
  nodeIds,
  inlineMode,
  setInlineMode,
  directMode = false,
  setDirectMode,
  onClose,
}) => {
  const { addBeamTruss, applyLoadToBeams, beamTrusses } = useBeamTrussStore();

  // UI state
  const [panelVisible, setPanelVisible] = useState(false);
  const [selectedLoadType, setSelectedLoadType] =
    useState<LoadType>("distributed1");
  const [loadValues, setLoadValues] = useState<LoadValues>({
    referenceNode: "S_Node",
  });

  const [successMessage, setSuccessMessage] = useState("");
  const [addStep, setAddStep] = useState<0 | 1 | 2>(0);
  const [addType, setAddType] = useState<"beam" | "node" | null>(null);

  const { selectedTrussType, setSelectedTrussType, applyLoadOnNode } =
    useTrussStore();

  // unify single or multiple node props into an array

  const [rotationValues, setRotationValues] = useState<{
    X: string | number;
    Y: string | number;
    Z: string | number;
  }>({
    X: "",
    Y: "",
    Z: "",
  });

  const [perpendicularValues, setPerpendicularValues] = useState<{
    X: string | number;
    Y: string | number;
    Z: string | number;
  }>({
    X: "",
    Y: "",
    Z: "",
  });

  const [isOpen, setIsOpen] = useState(false); // popup

  const modelStore = useModelStore();

  const { forceUnit, lengthUnit } = useUnitStore();

  // Helper functions for unit labels
  const getForceUnitLabel = (): string => {
    switch (forceUnit) {
      case "pound":
        return "P";
      case "kiloPound":
        return "KP";
      case "kilogram":
        return "KG";
      case "metricTon":
        return "MT";
      case "newton":
        return "N";
      case "decaNewton":
        return "DN";
      case "kiloNewton":
        return "KN";
      case "megaNewton":
        return "MN";
      default:
        return String(forceUnit).toUpperCase();
    }
  };

  const getLengthUnitLabel = (): string => {
    switch (lengthUnit) {
      case "inch":
        return "IN";
      case "foot":
        return "FT";
      case "millimeter":
        return "MM";
      case "centimeter":
        return "CM";
      case "decimeter":
        return "DM";
      case "meter":
        return "M";
      case "kilometer":
        return "KM";
      default:
        return String(lengthUnit).toUpperCase();
    }
  };

  const getBeamLength = (): number => {
    const beam = modelStore?.beams?.find((b) => b.id === beamId);
    const n1 = beam
      ? modelStore?.nodes?.find((n) => n.id === beam.startNodeId)
      : undefined;
    const n2 = beam
      ? modelStore?.nodes?.find((n) => n.id === beam.endNodeId)
      : undefined;
    if (n1 && n2) {
      const dx = n2.x - n1.x;
      const dy = n2.y - n1.y;
      const dz = n2.z - n1.z;
      return Math.sqrt(dx * dx + dy * dy + dz * dz);
    }
    return 0;
  };
  const generateBeamLoadName = (prefix: string) => {
    const matching = beamTrusses.filter((t) =>
      t.label.startsWith(prefix + "-")
    );
    const numbers = matching
      .map((t) => Number(t.label.split("-")[1]))
      .filter((n) => !isNaN(n));

    const next = numbers.length ? Math.max(...numbers) + 1 : 1;
    return `${prefix}-${next}`;
  };

  const generateNodeLoadName = (prefix: string) => {
    const trusses = useTrussStore.getState().nodeTrusses;

    const matching = trusses.filter((t) =>
      t.presetName?.startsWith(`${prefix}-`)
    );

    const numbers = matching
      .map((t) => Number(t.presetName?.split("-")[1]))
      .filter((n) => !isNaN(n));

    const next = numbers.length ? Math.max(...numbers) + 1 : 1;
    return `${prefix}-${next}`;
  };

  const resolvedNodeIds = React.useMemo<string[]>(() => {
    if (Array.isArray(nodeIds) && nodeIds.length) return nodeIds.map(String);
    if (nodeId) return [String(nodeId)];
    return [];
  }, [nodeId, nodeIds]);
  const hasTargetNodes = resolvedNodeIds.length > 0;

  useEffect(() => {
    if (inlineMode === "beam") {
      setAddStep(2);
      setAddType("beam");
      setPanelVisible(true);
    }

    if (inlineMode === "node") {
      setAddStep(2);
      setAddType("node");
      setIsOpen(true);
    }
  }, [inlineMode]);

  useEffect(() => {
    if (directMode) {
      setAddStep(1);
      setAddType(null);
      setPanelVisible(false);
      setIsOpen(false);
      setLoadValues({});
    }
  }, [directMode]);

  // if beamId provided and we have stored values for beam, auto-apply (optional behavior you had before)
  useEffect(() => {
    if (!beamId) return;

    const store = useBeamTrussStore.getState();

    const truss = store.beamTrusses.find((t) =>
      t.beamIds.includes(String(beamId))
    );

    if (!truss) return;

    setLoadValues({
      dx: truss.dx,
      dy: truss.dy,
      dz: truss.dz,

      dxStart: truss.dxLeft,
      dyStart: truss.dyLeft,
      dzStart: truss.dzLeft,

      dxEnd: truss.dxRight,
      dyEnd: truss.dyRight,
      dzEnd: truss.dzRight,

      fx: truss.fx,
      fy: truss.fy,
      fz: truss.fz,

      torque: truss.torque,
      torqueDirection: truss.torqueDirection,
    });
  }, [beamId]);

  const validateValues = (type: LoadType, v: LoadValues): boolean => {
    switch (type) {
      case "distributed1":
        return [v.dx, v.dy, v.dz].some((n) => typeof n === "number" && n !== 0);

      case "distributed2":
        return [
          v.dxStart,
          v.dyStart,
          v.dzStart,
          v.dxEnd,
          v.dyEnd,
          v.dzEnd,
        ].some((n) => typeof n === "number" && n !== 0);

      case "concentrated":
        return (
          [v.fx, v.fy, v.fz].some((n) => typeof n === "number" && n !== 0) ||
          typeof v.percentageFromNode === "number"
        );

      case "twisted":
        return typeof v.torque === "number" && v.torque !== 0;

      default:
        return false;
    }
  };

  const mapLoadTypeToBeamTrussType = (t: LoadType): BeamTrussType => {
    switch (t) {
      case "distributed1":
        return "distributed_uniform";
      case "distributed2":
        return "distributed_variable";
      case "concentrated":
        return "concentrated_load";
      case "twisted":
        return "twisted_load";
    }
  };

  const mapReferenceNode = (
    r?: ReferenceNode
  ): "node1" | "node2" | undefined => {
    if (r === "S_Node") return "node1";
    if (r === "E_Node") return "node2";
    return undefined;
  };

  const handleSavePreset = () => {
    // Auto-generate name
    const autoName = generateNodeLoadName("NLC");

    const parseValue = (val: string | number): number => {
      if (val === "" || val === null || val === undefined) return 0;
      return typeof val === "number" ? val : parseFloat(val) || 0;
    };

    const numericRotationValues = {
      X: parseValue(rotationValues.X),
      Y: parseValue(rotationValues.Y),
      Z: parseValue(rotationValues.Z),
    };

    const numericPerpendicularValues = {
      X: parseValue(perpendicularValues.X),
      Y: parseValue(perpendicularValues.Y),
      Z: parseValue(perpendicularValues.Z),
    };

    const hasNonZero =
      selectedTrussType === "regular"
        ? Object.values(rotationValues).some((v) => v !== 0)
        : Object.values(perpendicularValues).some((v) => v !== 0);

    if (!hasNonZero) {
      setSuccessMessage("At least one value must be non-zero.");
      setTimeout(() => setSuccessMessage(""), 2000);
      return;
    }

    // NORMAL MODE → PRESET ONLY
    if (!hasTargetNodes) {
      const store = useTrussStore.getState();

      // 🔑 bind preset context
      store.setSelectedPresetName(autoName);

      (["X", "Y", "Z"] as const).forEach((axis) => {
        const value =
          selectedTrussType === "regular"
            ? numericRotationValues[axis]
            : numericPerpendicularValues[axis];

        if (!value) return;

        store.addTruss(
          [], // preset-only
          selectedTrussType,
          axis,
          undefined,
          undefined,
          value,
          autoName // auto-generated label
        );
      });

      // 🔑 clear context
      store.setSelectedPresetName(null);

      setSuccessMessage("Preset saved");
      setRotationValues({ X: "", Y: "", Z: "" });
      setPerpendicularValues({ X: "", Y: "", Z: "" });
      setIsOpen(false);
      onClose();

      setTimeout(() => setSuccessMessage(""), 2000);
      return;
    }

    // INLINE MODE → APPLY TO NODE(S)
    applyLoadOnNode(
      resolvedNodeIds,
      numericRotationValues,
      numericPerpendicularValues
    );

    const store = useTrussStore.getState();

    const newlyCreated = store.nodeTrusses.filter(
      (t) =>
        t.nodeIds.some((id) => resolvedNodeIds.includes(id)) &&
        !t.presetName &&
        ((selectedTrussType === "regular" && t.type === "regular") ||
          (selectedTrussType === "perpendicular" && t.type === "perpendicular"))
    );

    newlyCreated.forEach((t) => {
      store.updateTruss(t.id, { presetName: autoName });
    });

    setSuccessMessage("Load applied to node");
    setRotationValues({ X: "", Y: "", Z: "" });
    setPerpendicularValues({ X: "", Y: "", Z: "" });
    setIsOpen(false);
    setInlineMode?.(null);
    setDirectMode?.(false);
    onClose();
    setTimeout(() => setSuccessMessage(""), 2000);
  };

  const handleSave = () => {
    if (!validateValues(selectedLoadType, loadValues)) {
      setSuccessMessage("Load values cannot be empty.");
      setTimeout(() => setSuccessMessage(""), 2000);
      return;
    }

    // Auto-generate name
    const autoName = generateBeamLoadName("BLC");

    const isDist2 = selectedLoadType === "distributed2";

    addBeamTruss(
      [],
      mapLoadTypeToBeamTrussType(selectedLoadType),
      undefined,

      isDist2 ? loadValues.dxStart : loadValues.dx,
      isDist2 ? loadValues.dyStart : loadValues.dy,
      isDist2 ? loadValues.dzStart : loadValues.dz,

      isDist2 ? loadValues.dxEnd : undefined,
      isDist2 ? loadValues.dyEnd : undefined,
      isDist2 ? loadValues.dzEnd : undefined,

      // concentrated
      loadValues.fx,
      loadValues.fy,
      loadValues.fz,

      mapReferenceNode(loadValues.referenceNode),
      loadValues.percentageFromNode,

      undefined,
      undefined,
      undefined,
      undefined,

      autoName,
      loadValues.torque,
      loadValues.torqueDirection
    );

    // reset UI
    setPanelVisible(false);
    setLoadValues({});
    onClose();
  };

  const handleSaveInline = () => {
    if (!validateValues(selectedLoadType, loadValues)) {
      setSuccessMessage("Load values cannot be empty.");
      setTimeout(() => setSuccessMessage(""), 2000);
      return;
    }

    // Auto-generate name
    const autoName = generateBeamLoadName("BLC");

    const appliedBeamIds =
      Array.isArray(beamIds) && beamIds.length
        ? beamIds.map(String)
        : beamId
          ? [String(beamId)]
          : [];

    const isDist2 = selectedLoadType === "distributed2";

    addBeamTruss(
      [],
      mapLoadTypeToBeamTrussType(selectedLoadType),
      undefined,

      isDist2 ? loadValues.dxStart : loadValues.dx,
      isDist2 ? loadValues.dyStart : loadValues.dy,
      isDist2 ? loadValues.dzStart : loadValues.dz,

      isDist2 ? loadValues.dxEnd : undefined,
      isDist2 ? loadValues.dyEnd : undefined,
      isDist2 ? loadValues.dzEnd : undefined,

      loadValues.fx,
      loadValues.fy,
      loadValues.fz,

      mapReferenceNode(loadValues.referenceNode),
      loadValues.percentageFromNode,

      undefined,
      undefined,
      undefined,
      undefined,

      autoName,
      loadValues.torque,
      loadValues.torqueDirection
    );

    const beamTrussesArr = useBeamTrussStore.getState().beamTrusses;
    const trussId = beamTrussesArr[beamTrussesArr.length - 1]?.id;

    if (trussId) {
      applyLoadToBeams(trussId, appliedBeamIds);
    }

    setPanelVisible(false);
    setLoadValues({});
    setInlineMode?.(null);
    onClose();
  };

  // Toggle buttons and content (same styling as your earlier panels)
  const renderToggleButtons = () => (
    <div className="p-2">
      <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-2">
        {(
          [
            "distributed1",
            "distributed2",
            "concentrated",
            "twisted",
          ] as LoadType[]
        ).map((type) => (
          <button
            key={type}
            onClick={() => setSelectedLoadType(type)}
            className={`flex-1 px-3 py-2 text-sm rounded-md transition-colors ${
              selectedLoadType === type
                ? "bg-blue-500 text-white shadow-sm"
                : "text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
            }`}
          >
            {formatLoadTypeName(type)}
          </button>
        ))}
      </div>
    </div>
  );

  const renderPanelcontent = () => {
    switch (selectedLoadType) {
      case "distributed1":
        return (
          <div className="space-y-5 p-3">
            {["dx", "dy", "dz"].map((f) => (
              <div
                key={f}
                className="flex items-center justify-between p-3 border border-gray-600 rounded-lg bg-gray-800 cursor-pointer"
              >
                <label className="text-sm font-medium w-12">
                  {f.toUpperCase()}
                </label>
                <div className="relative w-full max-w-[400px]">
                  <input
                    type="number"
                    value={(loadValues[f as keyof LoadValues] as number) ?? ""}
                    onChange={(e) =>
                      setLoadValues((prev) => ({
                        ...prev,
                        [f]:
                          e.target.value === ""
                            ? undefined
                            : parseFloat(e.target.value),
                      }))
                    }
                    className="w-full px-3 py-2 pr-20 text-sm border border-gray-600 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <span className="absolute right-10 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
                    {getForceUnitLabel()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        );

      case "distributed2":
        return (
          <div className="space-y-3 p-2">
            <div className="flex items-center justify-between p-3">
              <div className="w-12"></div>
              <div className="w-full max-w-[350px] grid grid-cols-2 text-center gap-2">
                <span className="text-xs font-semibold text-gray-300">
                  Start Node
                </span>
                <span className="text-xs font-semibold text-gray-300">
                  End Node
                </span>
              </div>
            </div>

            {/* Load Input Rows */}
            {["dx", "dy", "dz"].map((f) => (
              <div
                key={f}
                className="flex items-center justify-between p-3 border border-gray-600 rounded-lg bg-gray-800 cursor-pointer"
              >
                <label className="text-sm font-medium w-12 text-blue-300">
                  {f.toUpperCase()}
                </label>

                <div className="w-full max-w-[300px] grid grid-cols-2 gap-2">
                  <div className="relative">
                    <input
                      type="number"
                      value={
                        (loadValues[
                          `${f}Start` as keyof LoadValues
                        ] as number) ?? ""
                      }
                      onChange={(e) =>
                        setLoadValues((prev) => ({
                          ...prev,
                          [`${f}Start`]:
                            e.target.value === ""
                              ? undefined
                              : parseFloat(e.target.value),
                        }))
                      }
                      className="w-full px-2 py-1 pr-11 text-sm border border-gray-600 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center"
                      placeholder="Value"
                    />
                    <span className="absolute right-1 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none whitespace-nowrap">
                      {getForceUnitLabel()}
                    </span>
                  </div>

                  <div className="relative">
                    <input
                      type="number"
                      value={
                        (loadValues[`${f}End` as keyof LoadValues] as number) ??
                        ""
                      }
                      onChange={(e) =>
                        setLoadValues((prev) => ({
                          ...prev,
                          [`${f}End`]:
                            e.target.value === ""
                              ? undefined
                              : parseFloat(e.target.value),
                        }))
                      }
                      className="w-full px-2 py-1 pr-11 text-sm border border-gray-600 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center"
                      placeholder="Value"
                    />
                    <span className="absolute right-1 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none whitespace-nowrap">
                      {getForceUnitLabel()}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      case "concentrated": {
        const beamLength = getBeamLength();
        return (
          <div className="space-y-5 p-2">
            <div className="flex items-center space-x-2 cursor-pointer">
              {(["S_Node", "E_Node", "Center"] as ReferenceNode[]).map(
                (ref) => (
                  <label
                    key={ref}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="referenceNode"
                      value={ref}
                      checked={(loadValues.referenceNode ?? "S_Node") === ref}
                      onChange={(e) => {
                        const newRef = e.target.value as ReferenceNode;
                        setLoadValues((prev) => ({
                          ...prev,
                          referenceNode: newRef,
                          percentageFromNode:
                            newRef === "Center"
                              ? undefined
                              : prev.percentageFromNode,
                        }));
                      }}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 focus:ring-2"
                    />
                    <span className="text-sm text-gray-200">{ref}</span>
                  </label>
                )
              )}
            </div>

            {/* Beam Length Display */}
            <p className="ml-5 text-xs text-gray-400">
              Beam Length:{" "}
              <span className="font-semibold">{beamLength.toFixed(2)}</span>{" "}
              {getLengthUnitLabel()} {beamId ? `(Beam ${beamId})` : ""}
            </p>

            {/* Percentage Input */}
            {loadValues.referenceNode !== "Center" && (
              <div className="relative w-full max-w-[400px]">
                <input
                  type="number"
                  value={loadValues.percentageFromNode ?? ""}
                  onChange={(e) => {
                    let val = parseFloat(e.target.value);
                    if (!isNaN(val)) {
                      val = Math.min(Math.max(val, 0), 100);
                    }
                    setLoadValues((prev) => ({
                      ...prev,
                      percentageFromNode:
                        e.target.value === "" ? undefined : val,
                    }));
                  }}
                  className="w-full px-3 py-2 pr-16 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Percentage (%)"
                />
              </div>
            )}

            <hr className="border-gray-700 my-3" />

            {/* Force Inputs */}
            <div className="space-y-4 w-full max-w-[400px]">
              {/* FX */}
              <div className="relative">
                <span className="absolute inset-y-0 left-3 flex items-center text-red-400 text-sm font-semibold select-none">
                  FX
                </span>
                <input
                  type="number"
                  value={loadValues.fx ?? ""}
                  onChange={(e) =>
                    setLoadValues((prev) => ({
                      ...prev,
                      fx:
                        e.target.value === ""
                          ? undefined
                          : parseFloat(e.target.value),
                    }))
                  }
                  className="w-full px-10 pr-16 py-2 border rounded-md bg-gray-700 border-gray-600 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter value"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                  {getForceUnitLabel()}
                </span>
              </div>

              {/* FY & FZ */}
              <div className="flex space-x-3">
                {/* FY */}
                <div className="relative flex-1">
                  <span className="absolute inset-y-0 left-3 flex items-center text-green-400 text-sm font-semibold select-none">
                    FY
                  </span>
                  <input
                    type="number"
                    value={loadValues.fy ?? ""}
                    onChange={(e) =>
                      setLoadValues((prev) => ({
                        ...prev,
                        fy:
                          e.target.value === ""
                            ? undefined
                            : parseFloat(e.target.value),
                      }))
                    }
                    className="w-full px-10 pr-14 py-2 border rounded-md bg-gray-700 border-gray-600 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter value"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                    {getForceUnitLabel()}
                  </span>
                </div>

                {/* FZ */}
                <div className="relative flex-1">
                  <span className="absolute inset-y-0 left-3 flex items-center text-blue-400 text-sm font-semibold select-none">
                    FZ
                  </span>
                  <input
                    type="number"
                    value={loadValues.fz ?? ""}
                    onChange={(e) =>
                      setLoadValues((prev) => ({
                        ...prev,
                        fz:
                          e.target.value === ""
                            ? undefined
                            : parseFloat(e.target.value),
                      }))
                    }
                    className="w-full px-10 pr-14 py-2 border rounded-md bg-gray-700 border-gray-600 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter value"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                    {getForceUnitLabel()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      }

      case "twisted":
        return (
          <div className="space-y-4 p-3">
            <div className="w-full max-w-[400px]">
              <label className="block text-sm font-medium text-purple-600 dark:text-purple-400 mb-2">
                Torque
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={loadValues.torque ?? ""}
                  onChange={(e) =>
                    setLoadValues((prev) => ({
                      ...prev,
                      torque:
                        e.target.value === ""
                          ? undefined
                          : parseFloat(e.target.value),
                    }))
                  }
                  className="w-full px-3 py-2 pr-24 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Enter torque value"
                  step="0.1"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 pointer-events-none">
                  {getForceUnitLabel()}-{getLengthUnitLabel()}
                </span>
              </div>
            </div>

            {/* Rotation Direction */}
            <div className="w-full max-w-[400px]">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Rotation Direction
              </label>
              <div className="flex space-x-4">
                {["clockwise", "anticlockwise"].map((dir) => (
                  <label
                    key={dir}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="torqueDirection"
                      value={dir}
                      checked={loadValues.torqueDirection === dir}
                      onChange={() =>
                        setLoadValues((prev) => ({
                          ...prev,
                          torqueDirection: dir as "clockwise" | "anticlockwise",
                        }))
                      }
                      className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 focus:ring-purple-500 focus:ring-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">
                      {dir}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="text-xs text-gray-500 bg-purple-50 dark:bg-gray-700 p-2 rounded border border-purple-200 dark:border-purple-900 w-full max-w-[400px]">
              <p>
                <strong>Visualization:</strong> 75% circular purple arrow at
                beam midpoint
              </p>
              <p>
                <strong>Direction:</strong> Arrow wraps around beam in selected
                rotation direction
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderTrussTypeButtons = () => (
    <div className="flex">
      <button
        onClick={() => setSelectedTrussType("regular")}
        className={`flex-1 px-4 py-2 text-sm rounded-l-md transition-colors ${
          selectedTrussType === "regular"
            ? "bg-blue-500 text-white"
            : "bg-gray-600 text-gray-300 hover:bg-gray-500"
        }`}
      >
        Regular
      </button>

      <button
        onClick={() => setSelectedTrussType("perpendicular")}
        className={`flex-1 px-4 py-2 text-sm rounded-r-md transition-colors ${
          selectedTrussType === "perpendicular"
            ? "bg-blue-500 text-white"
            : "bg-gray-600 text-gray-300 hover:bg-gray-500"
        }`}
      >
        Perpendicular
      </button>
    </div>
  );

  const renderXYZInputs = () => (
    <div className="space-y-5 p-3 w-full">
      {(["X", "Y", "Z"] as const).map((axis) => (
        <div
          key={axis}
          className="flex items-center p-3 border border-gray-600 rounded-lg bg-gray-800 cursor-pointer w-full"
        >
          <span className="text-sm font-medium text-white w-12 flex-shrink-0">
            D{axis}
          </span>

          <div className="relative flex-1 ml-3">
            <input
              type="number"
              value={
                selectedTrussType === "regular"
                  ? rotationValues[axis]
                  : perpendicularValues[axis]
              }
              onChange={(e) => {
                const value = e.target.value;

                selectedTrussType === "regular"
                  ? setRotationValues((prev) => ({ ...prev, [axis]: value }))
                  : setPerpendicularValues((prev) => ({
                      ...prev,
                      [axis]: value,
                    }));
              }}
              onClick={(e) => e.stopPropagation()}
              className="w-full px-3 py-2 pr-20 text-sm border border-gray-600 rounded-md bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              step={0.1}
            />
            {/* Unit label */}
            <span className="absolute right-10 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
              {selectedTrussType === "regular"
                ? `${getForceUnitLabel()}-${getLengthUnitLabel()}`
                : getForceUnitLabel()}
            </span>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      {/* =============== NORMAL MODE =============== */}
      {!inlineMode && !setInlineMode ? (
        <>
          {!directMode && !setDirectMode && (
            <div className="border border-gray-600 rounded-lg p-4 shadow-md"></div>
          )}

          {addStep === 1 && (
            <DraggablePanel
              title="Select Load Target"
              initialPosition={{ x: 550, y: 200 }}
              onClose={() => {
                setAddStep(0);
                setDirectMode?.(false);
              }}
            >
              <div className="flex flex-col flex-dire h-full">
                {/* Options stacked vertically */}
                <div className="flex flex-col gap-6 p-6 justify-center items-center">
                  <div
                    onClick={() => {
                      setAddType("beam");
                      setAddStep(2);
                      setPanelVisible(true);
                    }}
                    className="w-60 flex items-center justify-center text-center gap-3 p-4 rounded cursor-pointer transition border border-gray-400 hover:border-blue-500"
                  >
                    <p className="font-medium">Beam</p>
                  </div>

                  <div
                    onClick={() => {
                      setAddType("node");
                      setAddStep(2);
                      setIsOpen(true);
                    }}
                    className="w-60 flex items-center justify-center text-center gap-3 p-4 rounded cursor-pointer transition border border-gray-400 hover:border-blue-500"
                  >
                    <p className="font-medium">Node</p>
                  </div>
                </div>
              </div>
            </DraggablePanel>
          )}

          {/* Beam Load Panel */}
          {panelVisible && addType === "beam" && (
            <DraggablePanel
              title={"Add New Load"}
              initialPosition={{ x: 550, y: 200 }}
              onClose={() => {
                setPanelVisible(false);

                setAddStep(0);
                if (typeof setDirectMode === "function") setDirectMode(false);
              }}
            >
              <div className="flex flex-col space-y-2">
                {renderToggleButtons()}
                {renderPanelcontent()}

                <div className="border-t">
                  <button
                    onClick={handleSave}
                    style={{ width: "95%" }}
                    className="m-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-600"
                  >
                    Save Load
                  </button>
                </div>
              </div>
            </DraggablePanel>
          )}

          {isOpen && addType === "node" && (
            <DraggablePanel
              initialPosition={{ x: 550, y: 200 }}
              title={`Add New Load`}
              style={{
                width: "430px",
              }}
              onClose={() => {
                setIsOpen(false);
                setAddStep(0);
                if (typeof setDirectMode === "function") setDirectMode(false);
              }}
            >
              <div className="overflow-y-auto overflow-x-hidden draggable-truss-panel">
                <div className="p-4 space-y-5">
                  {/* Type Toggle Buttons */}
                  {renderTrussTypeButtons()}
                  {/* Input Fields */}
                  {renderXYZInputs()}

                  {/* Save to Local Checkbox */}
                  {/* <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="saveToLocal"
                      checked={saveToLocal}
                      onChange={(e) => setSaveToLocal(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="saveToLocal" className="text-sm text-white">
                      Save to Predefined Loads
                    </label>
                  </div> */}

                  {/* Save Button */}
                  <div className="flex gap-2 pt-3 border-t mt-6">
                    <button
                      onClick={handleSavePreset}
                      className="w-full p-2 text-sm bg-green-500 text-white rounded hover:bg-green-600"
                    >
                      Save Load
                    </button>
                  </div>
                </div>
              </div>
            </DraggablePanel>
          )}

          {/* feedback */}
          {successMessage && (
            <div className="mt-2 text-green-600 text-sm">{successMessage}</div>
          )}
        </>
      ) : (
        /* =============== INLINE MODE =============== */
        <>
          {inlineMode === "beam" && panelVisible && (
            <DraggablePanel
              title={"Add New Load"}
              initialPosition={{ x: 550, y: 200 }}
              onClose={() => {
                setPanelVisible(false);

                setAddStep(0);
                setInlineMode?.(null);
              }}
            >
              <div className="flex flex-col space-y-2">
                {renderToggleButtons()}
                {renderPanelcontent()}
                {/* <div className="flex items-center space-x-2 m-3">
                  <input
                    type="checkbox"
                    id="saveToLocal"
                    checked={saveToLocal}
                    onChange={(e) => setSaveToLocal(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="saveToLocal" className="text-sm text-white">
                    Save to Predefined Loads
                  </label>
                </div> */}

                <div className="border-t">
                  <button
                    onClick={handleSaveInline}
                    style={{ width: "95%" }}
                    className="m-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-600"
                  >
                    Save Load
                  </button>
                </div>
              </div>
            </DraggablePanel>
          )}

          {inlineMode === "node" && isOpen && (
            <DraggablePanel
              initialPosition={{ x: 550, y: 200 }}
              title={`Add New Load`}
              style={{
                width: "400px",
              }}
              onClose={() => {
                setIsOpen(false);
                setAddStep(0);
                setInlineMode?.(null);
              }}
            >
              <div className="overflow-y-auto overflow-x-hidden draggable-truss-panel">
                <div className="p-4 space-y-5">
                  {renderTrussTypeButtons()}
                  {renderXYZInputs()}

                  {/* <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="saveToLocal"
                      checked={saveToLocal}
                      onChange={(e) => setSaveToLocal(e.target.checked)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="saveToLocal" className="text-sm text-white">
                      Save to Predefined Loads
                    </label>
                  </div> */}
                  <div className="pt-3 border-t">
                    <button
                      onClick={handleSavePreset}
                      className="w-full p-2 text-sm bg-green-500 text-white rounded hover:bg-green-600"
                    >
                      Save Load
                    </button>
                  </div>
                </div>
              </div>
            </DraggablePanel>
          )}
        </>
      )}
    </>
  );
};

export default PredefinedLoadManager;
